import React, { useEffect, useRef, useState } from 'react'

import './styles.scss'
import { Button, Col, Divider, Form, Input, Row, Select, InputNumber } from 'antd'
import { TAX_PERCENTAGE } from '../../../../../constants/finance'
import { countryList } from '../../../../../constants/itemTypes'
import usePageSettings from '../../../../../hooks/usePageSettings'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'

const FixedTourTravelerInfo = ({
    userTourData,
    setUserTourData,
    tourPackage,
    messageApi,
    prev,
    next,
    calculateTotal,
    calculateSubTotal,
    calculateDiscount,
    calculateTax,
    current,
}) => {
    const formRef = useRef();
    const selectedCurrency = usePageSettings()?.selectedCurrency;
    const [travelers, setTravelers] = useState([{ id: 1 }]);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (userTourData.userData) {
            formRef?.current?.setFieldsValue(userTourData.userData)
        }
    }, [userTourData.userData])

    const addTraveler = () => {
        if (travelers.length < tourPackage?.maxGroupSize) {
            setTravelers([...travelers, { id: travelers.length + 1 }]);
        }
    };

    const removeTraveler = () => {
        if (travelers.length > 1) {
            setTravelers(travelers.slice(0, -1));
        }
    };

    const handleGoNext = (values) => {
        if (!userTourData.startDate) {
            messageApi.open({
                type: 'error',
                message: 'Start Date is required',
                description: 'Please go back and select a start date for the tour.',
            })
            return
        }

        const travelerData = {};
        travelers.forEach((traveler, index) => {
            const travelerIndex = index + 1;
            travelerData[`traveler${travelerIndex}FirstName`] = values[`traveler${travelerIndex}FirstName`];
            travelerData[`traveler${travelerIndex}LastName`] = values[`traveler${travelerIndex}LastName`];
            travelerData[`traveler${travelerIndex}Email`] = values[`traveler${travelerIndex}Email`];
            travelerData[`traveler${travelerIndex}Phone`] = values[`traveler${travelerIndex}Phone`];
            travelerData[`traveler${travelerIndex}Country`] = values[`traveler${travelerIndex}Country`];
            travelerData[`traveler${travelerIndex}PassportNumber`] = values[`traveler${travelerIndex}PassportNumber`];
        });

        const updatedUserData = {
            ...values,
            ...travelerData,
            totalTravelers: travelers.length
        };

        setUserTourData({
            ...userTourData,
            userData: updatedUserData,
            adults: travelers.length,
            children: 0,
            infants: 0
        });

        next();
    }

    return (
        <div className='travelerDetails'>
            <div className='left'>
                <div className='tour_package_details'>
                    <div className='tour_package_details_header'>
                        <h2>{tourPackage?.packageName}</h2>
                        <p>{tourPackage?.durationDays} Days / {tourPackage?.durationNights} Nights</p>
                        <p>Start Date: {userTourData?.startDate}</p>
                    </div>
                </div>

                <div className='pricing_details'>
                    <h3>Pricing Details</h3>
                    <div className='pricing_details_content'>
                        <div className='pricing_row'>
                            <span>Package Price (for {tourPackage?.maxGroupSize} {tourPackage?.maxGroupSize > 1 ? 'people' : 'person'})</span>
                            <span>{calculateSubTotal()} {selectedCurrency || 'AUD'}</span>
                        </div>
                        {tourPackage?.discount > 0 && (
                            <div className='pricing_row discount'>
                                <span>Discount ({tourPackage?.discount}%)</span>
                                <span>- {calculateDiscount()} {selectedCurrency || 'AUD'}</span>
                            </div>
                        )}
                        <div className='pricing_row'>
                            <span>Tax ({TAX_PERCENTAGE}%)</span>
                            <span>{calculateTax()} {selectedCurrency || 'AUD'}</span>
                        </div>
                        <Divider />
                        <div className='pricing_row total'>
                            <span>Total</span>
                            <span>{calculateTotal()} {selectedCurrency || 'AUD'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className='right'>
                <div className='form_container'>
                    <h2>Traveler Information</h2>
                    <p>Please provide information for all travelers. At least one traveler is required.</p>

                    <div className='traveler_count_control'>
                        <span>Number of Travelers: {travelers.length}</span>
                        <div className='controls'>
                            <Button
                                icon={<MinusOutlined />}
                                onClick={removeTraveler}
                                disabled={travelers.length <= 1}
                                size="small"
                            />
                            <Button
                                icon={<PlusOutlined />}
                                onClick={addTraveler}
                                disabled={travelers.length >= tourPackage?.maxGroupSize}
                                size="small"
                            />
                        </div>
                    </div>

                    <Form
                        layout='vertical'
                        onFinish={handleGoNext}
                        ref={formRef}
                        requiredMark={false}
                    >
                        {travelers.map((traveler, index) => (
                            <div key={traveler.id} className='traveler_section'>
                                <h3>Traveler {index + 1} {index === 0 && '(Primary Contact)'}</h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}FirstName`}
                                            label='First Name'
                                            rules={[{ required: true, message: 'Please input first name!' }]}
                                        >
                                            <Input placeholder="First Name" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}LastName`}
                                            label='Last Name'
                                            rules={[{ required: true, message: 'Please input last name!' }]}
                                        >
                                            <Input placeholder="Last Name" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}Email`}
                                            label='Email'
                                            rules={[
                                                { required: true, message: 'Please input email!' },
                                                { type: 'email', message: 'Please input a valid email!' }
                                            ]}
                                        >
                                            <Input placeholder="Email Address" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}Phone`}
                                            label='Phone Number'
                                            rules={[{ required: true, message: 'Please input phone number!' }]}
                                        >
                                            <Input placeholder="Phone Number" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}Country`}
                                            label='Country'
                                            rules={[{ required: true, message: 'Please select country!' }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Select Country"
                                                options={countryList.map(country => ({ label: country, value: country }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={`traveler${index + 1}PassportNumber`}
                                            label='Passport Number'
                                            rules={[{ required: true, message: 'Please input passport number!' }]}
                                        >
                                            <Input placeholder="Passport Number" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {index < travelers.length - 1 && <Divider />}
                            </div>
                        ))}

                        <div className='form_actions'>
                            <Button
                                onClick={prev}
                                className='prev_btn'
                                size='large'
                            >
                                Back
                            </Button>
                            <Button
                                type='primary'
                                htmlType='submit'
                                className='next_btn'
                                size='large'
                            >
                                Continue to Confirmation
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

export default FixedTourTravelerInfo
